/**
 * Runtime formatting registry.
 *
 * P3-020: All display formatting goes through this registry.
 * No inline formatting in generic components; no hardcoded NPR formatting.
 *
 * Supports: money, quantity, percentage, date, datetime, status,
 * decimal, integer, and redacted placeholders.
 *
 * AD/BS dual-calendar rendering is coordinated with the i18n locale/calendar
 * settings. The BS conversion is provided as a thin shim; production accuracy
 * should be provided by a dedicated calendar package.
 */
import type { FormatType, FormatDescriptor } from '@finance/contracts';
import type { Locale, CalendarSystem, DateFormatMode } from '@finance/contracts';
import { getLanguageTag } from '../../i18n/locale-registry';
import { browserLogger } from '../logging/browser-logger';
import { adToBs, formatBsDate } from './bikram-sambat';

function getLocaleTag(ctx: FormatContext): string {
  return ctx.numberLocale ?? getLanguageTag(ctx.locale);
}

export interface FormatContext {
  locale: Locale;
  numberLocale?: string;
  calendar: CalendarSystem;
  /** Distinguishes Gregorian-only from user-selected dual-calendar output. */
  dateFormat?: DateFormatMode;
  currencyCode: string;
  timeZone?: string;
}

export const DEFAULT_CONTEXT: FormatContext = {
  locale: 'en',
  calendar: 'gregorian',
  currencyCode: 'NPR',
};

/** Validates persisted IANA zone identifiers at the formatting boundary. */
export function isValidTimeZone(value: string): boolean {
  if (!value.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value.trim() }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

/**
 * Return a stable ISO calendar day for an instant in the supplied IANA zone.
 * This is intentionally locale-independent because it is used for governed
 * business-date comparisons and persisted date-only form values.
 */
export function formatCalendarDateInTimeZone(value: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const valueFor = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(part => part.type === type)?.value ?? '';
  return `${valueFor('year')}-${valueFor('month')}-${valueFor('day')}`;
}

/** Resolve a local weekday (Sunday = 0) without leaking Intl into UI hooks. */
export function weekdayInTimeZone(value: Date, timeZone: string): number {
  const weekday = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' })
    .formatToParts(value)
    .find(part => part.type === 'weekday')?.value;
  return ({ Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 } as const)[
    weekday as 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat'
  ] ?? 0;
}

/**
 * Resolve the persisted browser preference envelope for legacy callers that
 * invoke the shared formatter outside React. Explicit context always wins;
 * this keeps descriptor-driven and hook-based consumers deterministic while
 * preventing bespoke pages from silently falling back to en-NP or the host
 * time zone after a preference update.
 */
function browserPreferenceContext(): Partial<FormatContext> {
  if (typeof document === 'undefined') return {};
  const root = document.documentElement;
  const numberLocale = root.dataset.numberFormat;
  const dateFormat = root.dataset.dateFormat;
  const timeZone = root.dataset.timeZone;
  const language = root.lang.toLowerCase();
  return {
    ...(language ? { locale: language.startsWith('ne') ? 'ne' : 'en' } : {}),
    ...(numberLocale === 'en-NP' || numberLocale === 'en-US' || numberLocale === 'en-IN'
      ? { numberLocale }
      : {}),
    ...(dateFormat === 'AD' || dateFormat === 'BS' || dateFormat === 'DUAL' ? { dateFormat } : {}),
    ...(dateFormat === 'BS' ? { calendar: 'bikram_sambat' } : {}),
    ...(timeZone ? { timeZone } : {}),
  };
}

export type Formatter = (
  value: unknown,
  descriptor: FormatDescriptor,
  ctx: FormatContext
) => string;

const MISSING_VALUE = '—';

function isNullish(value: unknown): value is null | undefined {
  return value === null || value === undefined || value === '';
}

function formatMoney(value: unknown, descriptor: FormatDescriptor, ctx: FormatContext): string {
  if (isNullish(value)) return MISSING_VALUE;
  const num = typeof value === 'string' ? Number(value) : Number(value);
  if (Number.isNaN(num)) return MISSING_VALUE;
  const fractionDigits = descriptor.minimumFractionDigits ?? 2;
  return new Intl.NumberFormat(getLocaleTag(ctx), {
    style: 'decimal',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: descriptor.maximumFractionDigits ?? fractionDigits,
  }).format(num);
}

function formatQuantity(value: unknown, descriptor: FormatDescriptor, ctx: FormatContext): string {
  if (isNullish(value)) return MISSING_VALUE;
  const num = typeof value === 'string' ? Number(value) : Number(value);
  if (Number.isNaN(num)) return MISSING_VALUE;
  return new Intl.NumberFormat(getLocaleTag(ctx), {
    minimumFractionDigits: descriptor.minimumFractionDigits ?? 4,
    maximumFractionDigits: descriptor.maximumFractionDigits ?? 4,
  }).format(num);
}

function formatDecimal(value: unknown, descriptor: FormatDescriptor, ctx: FormatContext): string {
  if (isNullish(value)) return MISSING_VALUE;
  const num = typeof value === 'string' ? Number(value) : Number(value);
  if (Number.isNaN(num)) return MISSING_VALUE;
  return new Intl.NumberFormat(getLocaleTag(ctx), {
    minimumFractionDigits: descriptor.minimumFractionDigits ?? 2,
    maximumFractionDigits: descriptor.maximumFractionDigits ?? 2,
  }).format(num);
}

function formatInteger(value: unknown, _descriptor: FormatDescriptor, ctx: FormatContext): string {
  if (isNullish(value)) return MISSING_VALUE;
  const num = typeof value === 'string' ? Number(value) : Number(value);
  if (Number.isNaN(num)) return MISSING_VALUE;
  return new Intl.NumberFormat(getLocaleTag(ctx), {
    maximumFractionDigits: 0,
  }).format(num);
}

function formatPercentage(
  value: unknown,
  descriptor: FormatDescriptor,
  ctx: FormatContext
): string {
  if (isNullish(value)) return MISSING_VALUE;

  // P0 guard: percentage API contract requires decimal fractions (e.g. 0.0654 = 6.54%).
  // Strings containing '%' are a contract violation. We handle them gracefully
  // for backward compatibility during transition, but they must be eliminated
  // at source (see portfolio-dashboard-aggregate.service.ts).
  let raw = value;
  if (typeof raw === 'string' && raw.includes('%')) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      browserLogger.warn(
        'format-contract',
        'Percentage value contains "%" — percentages must be decimal fractions at API boundaries.',
        { value: raw },
      );
    }
    raw = raw.replace('%', '');
    const parsed = Number(raw);
    if (!Number.isNaN(parsed)) {
      raw = parsed / 100;
    }
  }

  const num = typeof raw === 'string' ? Number(raw) : Number(raw);
  if (Number.isNaN(num)) return MISSING_VALUE;
  return new Intl.NumberFormat(getLocaleTag(ctx), {
    style: 'percent',
    minimumFractionDigits: descriptor.minimumFractionDigits ?? 2,
    maximumFractionDigits: descriptor.maximumFractionDigits ?? 2,
  }).format(num);
}

function parseDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function gregorianDateString(date: Date, includeTime: boolean, ctx: FormatContext): string {
  return new Intl.DateTimeFormat(getLocaleTag(ctx), {
    dateStyle: 'medium',
    ...(includeTime ? { timeStyle: 'short' } : {}),
    timeZone: ctx.timeZone,
  }).format(date);
}

/**
 * Bikram Sambat date conversion using the authoritative day-count registry.
 * This is the single source of truth for BS date rendering in the application.
 */
function bikramSambatString(date: Date, includeTime: boolean, ctx: FormatContext): string {
  const { year, month, day } = adToBs(date);
  const datePart = formatBsDate(year, month, day);
  if (!includeTime) return datePart;
  const timePart = date.toLocaleTimeString(getLocaleTag(ctx), {
    timeZone: ctx.timeZone,
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${datePart} ${timePart}`;
}

function dualCalendarString(date: Date, includeTime: boolean, ctx: FormatContext): string {
  return `${gregorianDateString(date, includeTime, ctx)} / ${bikramSambatString(date, includeTime, ctx)}`;
}

function formatDate(value: unknown, descriptor: FormatDescriptor, ctx: FormatContext): string {
  // ISO date-only is a calendar date. Formatting it as an instant makes the
  // displayed day depend on the viewer's timezone (for example, LA sees the
  // prior date for `2026-01-01T00:00:00Z`). Use UTC solely to give Intl a
  // stable calendar carrier, never the viewer timezone.
  const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = parseDate(value);
  if (!date) return MISSING_VALUE;
  const calendarCtx = dateOnly ? { ...ctx, timeZone: 'UTC' } : ctx;
  if (calendarCtx.dateFormat === 'DUAL') return dualCalendarString(date, false, calendarCtx);
  if (calendarCtx.calendar === 'bikram_sambat') {
    return bikramSambatString(date, false, calendarCtx);
  }
  const format = descriptor.dateFormat;
  if (format === 'ISO') return date.toISOString().split('T')[0];
  return gregorianDateString(date, false, calendarCtx);
}

function formatDateTime(value: unknown, descriptor: FormatDescriptor, ctx: FormatContext): string {
  const date = parseDate(value);
  if (!date) return MISSING_VALUE;
  if (ctx.dateFormat === 'DUAL') return dualCalendarString(date, true, ctx);
  if (ctx.calendar === 'bikram_sambat') {
    return bikramSambatString(date, true, ctx);
  }
  return gregorianDateString(date, true, ctx);
}

function formatStatus(value: unknown, _descriptor: FormatDescriptor, _ctx: FormatContext): string {
  if (isNullish(value)) return MISSING_VALUE;
  return String(value)
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const FORMATTERS: Record<FormatType, Formatter> = {
  currency: formatMoney,
  quantity: formatQuantity,
  percentage: formatPercentage,
  date: formatDate,
  datetime: formatDateTime,
  status: formatStatus,
  decimal: formatDecimal,
  integer: formatInteger,
};

export function formatValue(
  value: unknown,
  descriptor: FormatDescriptor | undefined,
  ctx: Partial<FormatContext> = {}
): string {
  if (!descriptor) return isNullish(value) ? MISSING_VALUE : String(value);
  const formatType = descriptor.formatType;
  const formatter = FORMATTERS[formatType];
  if (!formatter) return isNullish(value) ? MISSING_VALUE : String(value);
  return formatter(value, descriptor, {
    ...DEFAULT_CONTEXT,
    ...browserPreferenceContext(),
    ...ctx,
  });
}

export function createFormatDescriptor(
  formatType: FormatType,
  overrides?: Partial<FormatDescriptor>
): FormatDescriptor {
  return {
    formatType,
    ...overrides,
  };
}

export type { FormatType, FormatDescriptor };
